
import { EmailDomainsViewModel } from '@/entities/OrganizationalSettings/EmailDomainsViewModel';
import Common from '@/helper/Common';
import EmailBrandingHelper from '@/helper/EmailBrandingHelper';
import { FetchData, Root } from '@/main';
import useVuelidate from '@vuelidate/core';
import { minLength, required } from '@vuelidate/validators';
import { reactive } from 'vue';
import { Component, Prop, toNative, Vue, Watch } from 'vue-facing-decorator';
import ValidationSummary from '@/components/Common/ValidationSummary.vue';
import EmailListingsModel from '@/entities/OrganizationalSettings/EmailListingsModel';
import DomainStatusTypeEnum from '@/enums/DomainStatusTypeEnum';
import EmailDomainDrawerTypeEnum from '@/enums/EmailDomainDrawerTypeEnum';
import { DNSRecordModel } from '@/entities/OrganizationalSettings/DNSRecordModel';
import { DNSRecordDataModel } from '@/entities/OrganizationalSettings/DNSRecordDataModel';
@Component({
  components: {
    ValidationSummary,
  },
})
class EmailDomainManager extends Vue {
  @Prop() private domainList!: EmailDomainsViewModel;
  @Prop() selectedType!: number;
  @Prop() editEmailAddress!: EmailListingsModel;
  @Prop() private selectedDomianId?: number;
  private crossIcon = require('@/assets/Images/grey-cross-icon.svg');
  private globeIcon = require('@/assets/Images/globe-duotone-solid.svg');
  private infoIcon = require('@/assets/Images/i-icon.svg');
  private copyIcon: string = require('@/assets/Images/copy-icon-blue.svg');
  private refreshIcon = require('@/assets/Images/refresh-icon.svg');
  private deleteIcon = require('@/assets/Images/red-delete-icon.svg');
  private greenTickIcon = require('@/assets/Images/green-tick-icon.svg');
  private emailIcon = require('@/assets/Images/circle-envelope.svg');
  private atTheRateIcon = require('@/assets/Images/at-the-rate-icon.svg');
  private successStatusIcon = require('@/assets/Images/success-status-icon.svg');
  private copiedValue = '';
  private showAddDomainScreen = false;
  private isDomainTooltipVisible = false;
  private showEmailDomainsTable = false;
  private dnsRecordsTableData = EmailDomainsViewModel.createEmpty();
  private hasWhitespace = /\s/;
  private hasSpecialChars = /[^a-z0-9.-]/;
  private hasHyphenAtStartOrEnd = /(^-|-$|(\.-)|(-\.))/;
  private hasValidTLD = /\.[a-z]{2,}$/;
  private headerText = '';
  private localSelectedType = EmailBrandingHelper.DomainDrawer;
  private domainNameMaxLength = EmailBrandingHelper.DomainNameMaxLength;
  private emailPrefixMaxLength = EmailBrandingHelper.EmailPrefixMaxLength;
  private defaultDomainName = EmailBrandingHelper.DefaultDomainName;
  private domainInfoString = EmailBrandingHelper.DomainInfoString;
  private currentDomains: string[] = [];



  // Validation Summary flag
  private isValidationSummaryVisible = false;
  private validationSummaryHeading = '';
  private validationErrorFields: string[] = [];

  private state = reactive({
    emailPrefix: '',
    senderName: '',
    domainName: '',
  });

  private rules = {
    emailPrefix: {
      required,
      minLength: minLength(1),
    },
    senderName: {
      required,
      minLength: minLength(1),
    },
    domainName: {
      required,
      minLength: minLength(1),
    },
  };

  private status(validation: { $error: any; $dirty: any }) {
    return {
      error: validation.$error,
      dirty: validation.$dirty,
    };
  }
  private v$ = useVuelidate(this.rules, this.state);

  // Getter for showing footer section buttons
  public get showFooterTab(){
    return this.showEmailDomainsTable || this.showAddDomainScreen || this.dnsRecordsTableData.Domains.length > 0 || this.selectedDomianId === 0;
  }

  // Getter for showing Add/Edit email address section
  public get showAddEmailSection(){
    return this.localSelectedType === EmailBrandingHelper.EmailDrawer && (this.dnsRecordsTableData.Domains.length > 0 || this.selectedDomianId === 0);
  }

  // Getter for showing Add Domain name screen
  public get showAddDomainNameScreen(){
    return this.showAddDomainScreen && this.localSelectedType === EmailBrandingHelper.DomainDrawer && this.dnsRecordsTableData.Domains.length === 0 && this.selectedDomianId !== 0;
  }

  // Getter for showing Empty screen (No domain added yet)
  public get showNoDomainAddedScreen(){
    return this.dnsRecordsTableData.Domains.length === 0 && !this.showAddDomainScreen && this.selectedDomianId !== 0;
  }

  // Getter for showing Domain DNS records table
  public get showDomainRecordsTable(){
    return (this.dnsRecordsTableData.Domains.length > 0 && this.localSelectedType === EmailBrandingHelper.DomainDrawer) || this.showEmailDomainsTable;
  }

  // Getter for showing Add Domian Name button
  public get showAddDomainButton(){
    return this.dnsRecordsTableData.Domains.length === 0 && this.selectedDomianId !== 0;
  }

  // Getter for showing Add Email address button
  public get showAddEmailAddressButton(){
    return this.localSelectedType === EmailBrandingHelper.EmailDrawer || this.selectedDomianId === 0;
  }

  // Getter for Drawer type
  public get DrawerType(): typeof EmailDomainDrawerTypeEnum {
    return EmailDomainDrawerTypeEnum;
  }

  // Getter for domain status
  public get DomainStatus(): typeof DomainStatusTypeEnum {
    return DomainStatusTypeEnum;
  }

  private mounted(){
    this.localSelectedType = this.selectedType;
    this.dnsRecordsTableData = this.domainList;
    this.InitializeFormData();
    if(this.dnsRecordsTableData.Domains.length > 0){
      const [domain] = this.dnsRecordsTableData.Domains;
      if(domain.OverallDomainStatus === DomainStatusTypeEnum.Broken){
        this.validationErrorFields = [];
        this.isValidationSummaryVisible = true;
        this.validationSummaryHeading = this.$t('Error').toString();
        this.validationErrorFields.push(
          this.$t('OrgSettings.EmailBranding.BrokenDNSRecordErrorField1').toString(),
        );
      }
    }
    if(this.localSelectedType === EmailBrandingHelper.DomainDrawer){
      this.headerText = this.$t('OrgSettings.EmailBranding.Domain').toString();
    }else{
      this.headerText = this.$t('OrgSettings.EmailBranding.EmailAddress').toString();
    }
  }

  // Initilize the form
  private InitializeFormData(){
    if (this.editEmailAddress && this.editEmailAddress.EmailId !== 0) {
      this.state.senderName = this.editEmailAddress.SenderName;

      // Split email to get prefix and domain (e.g., info @ domain.com)
      const emailParts = this.editEmailAddress.EmailAddress.split('@');
      this.state.emailPrefix = emailParts[0];
      this.state.domainName = emailParts[1] || '';
    } else if (this.localSelectedType === EmailBrandingHelper.EmailDrawer) {
      this.state.senderName = '';
      this.state.emailPrefix = '';

      // Find the specific domain name using the selectedDomianId passed from parent
      const selectedDomain = this.domainList.Domains.find((d) => d.Id === this.selectedDomianId);

      if (selectedDomain) {
        this.state.domainName = selectedDomain.DomainName;
      } else if (this.domainList.Domains.length > 0) {
      // Fallback: use first domain if no specific ID matched
        this.state.domainName = this.domainList.Domains[0].DomainName;
      } else {
        this.state.domainName = '';
      }
    }
  }

  // Convert the domain name to lowercase on input
  private ConvertInputToLowerCase(val: string){
    return val ? val.toLowerCase() : '';
  }

  // Get placeholder text
  private SetPlaceholderText(){
    return this.$t('OrgSettings.EmailBranding.AddDomainPlaceholder',[EmailBrandingHelper.DomainTooltipPlaceholderEmailAddress]).toString();
  }

  // Emit to close the drawer
  private Close(){
    Root.emit('close-drawer');
  }

  // Show hide the domain tooltip
  private GetInformationString(val: boolean, type: string) {
    if (type === 'domain') {
      this.isDomainTooltipVisible = val;
    }
  }
  // Copy to clipboard
  private HandleCopyClick(data: string){
    Common.copyPublicLinkToClipboard(data);
    this.copiedValue = data;

    setTimeout(()=>{
      this.copiedValue = '';
    },1000);
  }

  // Format HostData field
  private FormatHostData(hostData: string): string {
    if (!hostData) {
      return '';
    }
    try {
      const parsedString = JSON.parse(hostData);
      if (Array.isArray(parsedString)) {
        return parsedString.join(', ');
      }
      return String(parsedString);
    } catch {
      return hostData;
    }
  }

  // Copy table records to clipboard
  private HandleCopyDNSRecords(data: DNSRecordModel, domainName: string){
    const records = [data.Spf, data.Dkim1, data.Dkim2, data.Dmarc]
      .filter((record): record is DNSRecordDataModel => record !== null);

    const recordData = records
      .map((record) => `${record.RecordType} ${record.HostName} ${record.HostData}`)
      .join('\n');

    Common.copyPublicLinkToClipboard(recordData);
    this.copiedValue = 'dns-records-' + domainName;

    setTimeout(() => {
      this.copiedValue = '';
    }, 1000);
  }

  // Function closing validation summary
  private CloseValidationSummary(visible: boolean) {
    this.isValidationSummaryVisible = visible;
  }

  // Show input fields
  private HandleAddDomain(){
    this.showAddDomainScreen = true;
    this.state.domainName = '';
    this.localSelectedType = EmailBrandingHelper.DomainDrawer;
    this.headerText = this.$t('OrgSettings.EmailBranding.AddDomainTextLine1').toString();
  }

  // Display premium feature not enabled text
  private GetDomainTooltipText() {
    return this.$t('OrgSettings.EmailBranding.DomainTooltipInfo', [EmailBrandingHelper.DomainTooltipPlaceholderEmailAddress]).toString();
  }

  // Function to create a new domain
  private CreateDomain(val: any){
    val.$touch();
    this.validationErrorFields = [];
    const hasDomainNameValidationErrors = this.ValidateInput(this.state.domainName);
    if(val.domainName!.$error === true){
      this.isValidationSummaryVisible = true;
      this.validationErrorFields.push(
        this.$t('OrgSettings.EmailBranding.DomainName').toString(),
      );
    } else if(hasDomainNameValidationErrors){
      this.isValidationSummaryVisible = true;
      this.validationErrorFields.push(hasDomainNameValidationErrors);
    } else{
      FetchData('/CustomDomains/RegisterDomain', JSON.stringify({
        domain: this.state.domainName,
      })).then(() => {
        this.$emit('dns-records');
        this.showAddDomainScreen = false;
        this.showEmailDomainsTable = true;
      }).catch(()=>{
      // console error if any
      });
    }
  }

  // Function to add new email address
  private AddEmailAddress(val: any){
    val.$touch();
    this.validationErrorFields = [];
    const hasEmailPrefixValidationErrors = this.ValidateInput(this.state.emailPrefix);

    if(val.emailPrefix!.$error === true){
      this.isValidationSummaryVisible = true;
      this.validationErrorFields.push(
        this.$t('OrgSettings.EmailBranding.EmailPrefix').toString(),
      );
    } else if(hasEmailPrefixValidationErrors){
      this.isValidationSummaryVisible = true;
      this.validationErrorFields.push(hasEmailPrefixValidationErrors);
    } else if(val.senderName!.$error === true){
      this.isValidationSummaryVisible = true;
      this.validationErrorFields.push(
        this.$t('OrgSettings.EmailBranding.SendersName').toString(),
      );
    } else{
      FetchData('/CustomDomains/SaveUpdateEmail', JSON.stringify({
        emailAddress: this.state.emailPrefix.concat('@',this.state.domainName),
        senderName: this.state.senderName,
        emailId: this.editEmailAddress.EmailId === 0 ? 0 : this.editEmailAddress.EmailId,
      })).then(() => {
        this.$emit('reload-email-listing');
        this.Close();
      }).catch(()=>{
      // console error if any
      });
    }
  }

  // Function to validate domain name or email prefix validations
  private ValidateInput(value: string): string | null {
    if (this.hasWhitespace.test(value)) {
      return this.$t('OrgSettings.EmailBranding.WhitespaceValidationMessage').toString();
    }

    if (this.hasSpecialChars.test(value)) {
      return this.$t('OrgSettings.EmailBranding.SpecialCharactersValidationMessage').toString();
    }

    if (this.hasHyphenAtStartOrEnd.test(value)) {
      return this.$t('OrgSettings.EmailBranding.HypenatedValidationMessage').toString();
    }

    if (this.localSelectedType === EmailBrandingHelper.DomainDrawer) {
      if (!this.hasValidTLD.test(value)) {
        return this.$t('OrgSettings.EmailBranding.InValidDomainNameValidationMessage').toString();
      }
      if (this.CheckIfDomainAlreadyExists(value)) {
        return this.$t('OrgSettings.EmailBranding.DomainAlreadyExistsValidationMessage').toString();
      }
    }

    return null;
  }

  // Check if the domain name already exists in the current domain listing
  private CheckIfDomainAlreadyExists(domainName: string){
    const existingDomains = this.dnsRecordsTableData.Domains.map((domain) => domain.DomainName);
    existingDomains.push(this.defaultDomainName);
    return existingDomains.includes(domainName);
  }

  // Function to verify the status of domain
  private VerifyDomainStatus(domainId: number, domainName: string){
    this.$emit('verify-domain-status', domainId, domainName);
  }

  // Function to delete a domain record
  private DeleteDomainData(domainID: number){
    this.$emit('open-delete-domain-modal', domainID);
  }

  // Set Domain status
  private SetDomainStatus(isValid: boolean, domainStatus: number){
    let status = '';
    if(isValid){
      status = EmailBrandingHelper.DomainVerifiedStatus;
    }else{
      if(domainStatus === DomainStatusTypeEnum.Broken && !isValid){
        status = EmailBrandingHelper.DomainErrorStatus;
      }else{
        status = EmailBrandingHelper.DomainPendingStatus;
      }
    }
    return status;
  }

  // Get Domain Status Text
  private SetDomainStatusText(status: number){
    let domainVerificationText = '';
    if(status === DomainStatusTypeEnum.Pending){
      domainVerificationText = this.$t('OrgSettings.EmailBranding.DomainVerificationPendingText').toString();
    }else{
      domainVerificationText = this.$t('OrgSettings.EmailBranding.DomainVerificationVerifiedText').toString();
    }
    return domainVerificationText;
  }

  // Set Overall Domain Status
  private SetOverallDomainStatus(domainStatus: number){
    let status = '';
    if(domainStatus === DomainStatusTypeEnum.Pending){
      status = EmailBrandingHelper.DomainNotVerifiedStatus;
    }else if(domainStatus === DomainStatusTypeEnum.Verified){
      status = EmailBrandingHelper.DomainVerifiedStatus;
    }else{
      status = EmailBrandingHelper.DomainErrorStatus;
    }
    return status;
  }

  // Set css class for domain status
  private GetDomainStatusCssClass(status: number) {
    if (status === DomainStatusTypeEnum.Verified) {
      return 'verified-roundpill';
    }
    if (status === DomainStatusTypeEnum.Pending) {
      return 'unverified-roundpill';
    }
    if (status === DomainStatusTypeEnum.Broken) {
      return 'error-roundpill';
    }
    return '';
  }

  // Set css class for domain records css class
  private GetRecordStatusCssClass(isValid: boolean, status: number){
    if(isValid){
      return 'success-status-pill';
    }
    if(!isValid && status === DomainStatusTypeEnum.Pending){
      return 'unverified-tab';
    }
    if(!isValid && status === DomainStatusTypeEnum.Broken){
      return 'error-tab';
    }
  }

  // Watch to monitor prop changes
  @Watch('domainList')
  private CheckChangeInDomainListing(){
    this.dnsRecordsTableData = this.domainList;
  }

}
export default toNative(EmailDomainManager);

